import {CabInitiatief, CabPeriod} from './index.js';
import {DashboardReeksEnum, DashboardTargetRuleEnum, InitiatiefFaseEnum, WaardeTypeEnum} from './enums&types.js';

export const DASHBOARD_START_PERIOD: string = '2023-Q1';

export type StatusLabel = 'gehaald' | 'gemist' | 'gestart' | 'onbekend';

// organisation and theme names are compared without accents and case: the data has names with accents, such as Allevo (DZV) spelled with an e-acute
export function normalizeName(s: string): string {
    return s.normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
}

export enum ToegekendStatusEnum {
    reedsToegekend             = 'Reeds toegekend',
    verwachtExtraToegekend     = 'Verwacht extra toegekend',
    verwachtNietExtraToegekend = 'Verwacht niet extra toegekend',
    onbekend                   = 'Onbekend',
}

export const TOEGEKEND_STATUS_ORDER: ToegekendStatusEnum[] = [
    ToegekendStatusEnum.reedsToegekend,
    ToegekendStatusEnum.verwachtExtraToegekend,
    ToegekendStatusEnum.verwachtNietExtraToegekend,
    ToegekendStatusEnum.onbekend,
];

// one entry selects the report items of an organisation, optionally limited to one theme
export type DashboardScope = {
    organisatie: string;
    thema?: string;
};

export type DashboardOrganisatie = {
    label: string;
    kort: string;
    scope: DashboardScope[];
};

const DZV_LEDEN: string[] = [
    'Zorg In Balans',
    'Allevo (DZV)',
    'Amarijn (DZV)',
    'SVRZ (DZV)',
    'WVO Zorg (DZV)',
    'ZorgSaam (DZV)',
    'Zorggroep ter Weel (DZV)',
    'Zorgstroom (DZV)',
];

// the organisation groups of the dashboard, see the design spec section 7.8 (to be confirmed by Nicole)
export const DASHBOARD_ORGANISATIES: DashboardOrganisatie[] = [
    {label: 'ADRZ',           kort: 'ADRZ',       scope: [{organisatie: 'ADRZ'}]},
    {label: 'Emergis',        kort: 'Emergis',    scope: [{organisatie: 'Emergis'}]},
    {label: 'Kerngezond',     kort: 'KG',         scope: [{organisatie: 'ZZC / Overstijgend', thema: 'Kerngezond'}]},
    {label: 'SVRZ',           kort: 'SVRZ',       scope: [{organisatie: 'SVRZ'}]},
    {label: 'TKHA',           kort: 'TKHA',       scope: [{organisatie: 'Huisartsen'}]},
    {label: 'Zorg In Balans', kort: 'ZiB',        scope: DZV_LEDEN.map(o => ({organisatie: o}))},
    {label: 'ZorgSaam',       kort: 'ZS',         scope: [{organisatie: 'ZorgSaam'}]},
    {label: 'Huisartsen',     kort: 'Huisartsen', scope: [{organisatie: 'Huisartsen'}]},
];

//////////////////////////////////// quarters ////////////////////////////////////

// sortable number for a 'YYYY-Qn' string
export function quarterKey(quarter: string): number {
    const p: CabPeriod = CabPeriod.ofString(quarter);
    return p.year * 4 + p.num;
}

// every quarter from 'from' up to and including 'to'
export function listQuarters(from: string, to: string): string[] {
    const result: string[] = [];
    const last: number     = quarterKey(to);
    let p: CabPeriod       = CabPeriod.ofString(from);
    while (p.year * 4 + p.num <= last) {
        result.push(p.toString());
        p = p.nextPeriod();
    }
    return result;
}

//////////////////////////////////// api payloads ////////////////////////////////////

export type DashboardPeriods = {
    kwartalen: string[];
    laatste: string;
    groepen: string[]; // the organisation group labels this user may pick
};

export type KpiRowType   = 'KPI' | 'Mijlpaal';
export type KpiTypeFilter = 'KPI' | 'Mijlpaal' | 'Alle';

export type KpiRow = {
    kpiUuid: string;
    vecozoId: string;
    type: KpiRowType;
    naam: string;
    thema: string;
    organisatie: string;
    kwartaal: string;
    toegekendStatus: ToegekendStatusEnum;
    statusLabel: StatusLabel;
    bedrag: number;
    toelichting: string;
};

export type KpisPage = {
    rows: KpiRow[];
};

export type KpiRowFilter = {
    type: KpiTypeFilter;
    statuses: ToegekendStatusEnum[];
    statusLabels: string[];
    kwartalen: string[];
    zoek: string;
};

export type KpiStatusTotal = {
    status: ToegekendStatusEnum;
    bedrag: number;
    aantalKpis: number;
    aantalMijlpalen: number;
};

//////////////////////////////////// kpi rows ////////////////////////////////////

function kpiRowMatchesSearch(row: KpiRow, zoek: string): boolean {
    const z: string = zoek.trim().toLowerCase();
    if (z === '') {
        return true;
    }
    return [row.vecozoId, row.naam, row.thema, row.organisatie, row.toelichting].some(v => v.toLowerCase().includes(z));
}

// every filter except the toegekend status, the status cards show totals over these rows
export function filterKpiRowsForTotals(rows: KpiRow[], f: KpiRowFilter): KpiRow[] {
    return rows.filter(r =>
        (f.type === 'Alle' || r.type === f.type)
        && (f.statusLabels.length === 0 || f.statusLabels.includes(r.statusLabel))
        && (f.kwartalen.length === 0 || f.kwartalen.includes(r.kwartaal))
        && kpiRowMatchesSearch(r, f.zoek),
    );
}

export function filterKpiRows(rows: KpiRow[], f: KpiRowFilter): KpiRow[] {
    return filterKpiRowsForTotals(rows, f).filter(r => f.statuses.length === 0 || f.statuses.includes(r.toegekendStatus));
}

export function totalsByStatus(rows: KpiRow[]): KpiStatusTotal[] {
    return TOEGEKEND_STATUS_ORDER.map(status => {
        const ofStatus: KpiRow[] = rows.filter(r => r.toegekendStatus === status);
        return {
            status         : status,
            bedrag         : ofStatus.reduce((sum, r) => sum + r.bedrag, 0),
            aantalKpis     : ofStatus.filter(r => r.type === 'KPI').length,
            aantalMijlpalen: ofStatus.filter(r => r.type === 'Mijlpaal').length,
        };
    });
}

// the status labels selected by default: all but onbekend
export function defaultStatusLabels(rows: KpiRow[]): string[] {
    return [...new Set(rows.map(r => r.statusLabel))].filter(l => l !== 'onbekend').sort();
}

//////////////////////////////////// series ////////////////////////////////////

export const ZZC_TOTAAL_LABEL: string         = 'ZZC totaal';
export const ZZC_TOTAAL_SCOPE: DashboardScope = {organisatie: 'ZZC / Overstijgend', thema: 'N.v.t.'};

export const TOEGANKELIJKHEID_KEUZE: DashboardReeksEnum[] = [
    DashboardReeksEnum.adherentie,
    DashboardReeksEnum.verwijzingenMsz,
    DashboardReeksEnum.verwijzingenGgz,
];
export const TOEGANKELIJKHEID_REEKSEN: DashboardReeksEnum[] = [
    DashboardReeksEnum.wachttijdenTreeknorm,
    ...TOEGANKELIJKHEID_KEUZE,
    DashboardReeksEnum.noni,
];
export const SCORE_REEKSEN: DashboardReeksEnum[] = [
    DashboardReeksEnum.medewerkerstevredenheid,
    DashboardReeksEnum.klanttevredenheid,
];
export const QUADRUPLE_AIM_REEKSEN: DashboardReeksEnum[] = [
    DashboardReeksEnum.volume,
    DashboardReeksEnum.productiviteit,
    DashboardReeksEnum.omzet,
    DashboardReeksEnum.schadelast,
    ...SCORE_REEKSEN,
];

export type ReeksPunt = {
    kwartaal: string;
    waarde: number;
};

export type ReeksTarget = {
    rule: DashboardTargetRuleEnum;
    value?: number;
    omschrijving: string;
};

// one series: the values of one reeks for one organisation group, oldest quarter first
export type ReeksSerie = {
    organisatie: string;
    reeks: DashboardReeksEnum;
    waardeType: WaardeTypeEnum;
    punten: ReeksPunt[];
    target?: ReeksTarget;
};

export type ReeksNotitie = {
    organisatie: string;
    reeks: DashboardReeksEnum;
    kwartaal: string;
    toelichting: string;
};

export type ReeksenPage = {
    series: ReeksSerie[];
    zzc: ReeksSerie[];
    notities: ReeksNotitie[];
};

//////////////////////////////////// colours (design spec 7.1) ////////////////////////////////////

export enum StatusKleur {
    groen = 'groen',
    amber = 'amber',
    rood  = 'rood',
    grijs = 'grijs',
}

export const KLEUR_BALK: Record<StatusKleur, string> = {
    groen: '#a4c130',
    amber: '#e59a00',
    rood : '#e85c5c',
    grijs: '#BFBFBF',
};
export const KLEUR_LIJN: string   = '#2b4371';
export const KLEUR_TARGET: string = '#a4c130';

// cell colours of the status matrices (the light variants)
export const KLEUR_CEL: Record<StatusKleur, string> = {
    groen: '#D1E48D',
    amber: '#F7E4AB',
    rood : '#F48686',
    grijs: '#BFBFBF',
};

// ratio inverted (max formatie): at most 1 groen, up to 1.1 amber, higher rood
export function ratioInvertedKleur(ratio: number | undefined): StatusKleur {
    if (ratio === undefined) {
        return StatusKleur.grijs;
    }
    if (ratio <= 1) {
        return StatusKleur.groen;
    }
    if (ratio <= 1.1) {
        return StatusKleur.amber;
    }
    return StatusKleur.rood;
}

// cost rule: realisatie / target - 1, at least -0.01 groen, at least -0.10 amber, lower rood
export function kostenKleur(realisatie: number | undefined, target: number | undefined): StatusKleur {
    if (realisatie === undefined || target === undefined || target === 0) {
        return StatusKleur.grijs;
    }
    const verschil: number = Math.round((realisatie / target - 1) * 1e6) / 1e6; // rounded to avoid float artifacts at the -0.01/-0.10 boundaries
    if (verschil >= -0.01) {
        return StatusKleur.groen;
    }
    if (verschil >= -0.10) {
        return StatusKleur.amber;
    }
    return StatusKleur.rood;
}

// uitputting rule: used / available, below 0.9 no colour (grijs), 0.9 and up amber, above 1 rood
export function uitputtingKleur(used: number, available: number): StatusKleur {
    if (available === 0) {
        return StatusKleur.grijs;
    }
    const uitputting: number = used / available;
    if (uitputting > 1) {
        return StatusKleur.rood;
    }
    if (uitputting >= 0.9) {
        return StatusKleur.amber;
    }
    return StatusKleur.grijs;
}

// text status: gehaald groen, gemist rood, anything else falls back to the given rule colour
export function statusKleurVanLabel(label: StatusLabel, fallback: StatusKleur): StatusKleur {
    if (label === 'gehaald') {
        return StatusKleur.groen;
    }
    if (label === 'gemist') {
        return StatusKleur.rood;
    }
    return fallback;
}

// ratio rule: 1 and up groen, 0.9 and up amber, lower rood; no ratio grijs
export function ratioKleur(ratio: number | undefined): StatusKleur {
    if (ratio === undefined) {
        return StatusKleur.grijs;
    }
    if (ratio >= 1) {
        return StatusKleur.groen;
    }
    if (ratio >= 0.9) {
        return StatusKleur.amber;
    }
    return StatusKleur.rood;
}

// groei t.o.v. de eerste meting: strictly more than 1 is groen
export function groeiKleur(ratio: number | undefined): StatusKleur {
    if (ratio !== undefined && ratio > 1) {
        return StatusKleur.groen;
    }
    if (ratio !== undefined && ratio >= 0.9) {
        return StatusKleur.amber;
    }
    return ratio === undefined ? StatusKleur.grijs : StatusKleur.rood;
}

export function kwartaalEenJaarEerder(kwartaal: string): string {
    const p: CabPeriod = CabPeriod.ofString(kwartaal);
    p.year             = p.year - 1;
    return p.toString();
}

function verhouding(teller: number, noemer: number | undefined): number | undefined {
    return noemer === undefined || noemer === 0 ? undefined : teller / noemer;
}

export type TrendStatus = {
    kleur: StatusKleur;
    ratio: number | undefined;
};

// the status of the last point of a series under its target rule; undefined without a target or points
export function trendStatus(serie: ReeksSerie): TrendStatus | undefined {
    if (!serie.target || serie.punten.length === 0) {
        return undefined;
    }
    const laatste: ReeksPunt = serie.punten[serie.punten.length - 1];
    switch (serie.target.rule) {
        case DashboardTargetRuleEnum.groeiTovEersteMeting: {
            const ratio: number | undefined = verhouding(laatste.waarde, serie.punten[0].waarde);
            return {kleur: groeiKleur(ratio), ratio};
        }
        case DashboardTargetRuleEnum.minimaalGelijkVorigJaar: {
            const vorig: ReeksPunt | undefined = serie.punten.find(p => p.kwartaal === kwartaalEenJaarEerder(laatste.kwartaal));
            const ratio: number | undefined    = verhouding(laatste.waarde, vorig?.waarde);
            return {kleur: ratioKleur(ratio), ratio};
        }
        case DashboardTargetRuleEnum.vasteWaarde: {
            const ratio: number | undefined = verhouding(laatste.waarde, serie.target.value);
            return {kleur: ratioKleur(ratio), ratio};
        }
    }
}

// bar colours against the first measurement; counts only get colours under a growth rule
export function puntKleuren(serie: ReeksSerie): string[] {
    const groeiRegel: boolean = serie.target !== undefined && serie.target.rule !== DashboardTargetRuleEnum.vasteWaarde;
    if (serie.punten.length === 0 || (serie.waardeType !== WaardeTypeEnum.percentage && !groeiRegel)) {
        return serie.punten.map(() => KLEUR_LIJN);
    }
    const eerste: number = serie.punten[0].waarde;
    return serie.punten.map(p => KLEUR_BALK[ratioKleur(verhouding(p.waarde, eerste))]);
}

//////////////////////////////////// initiatieven (design spec 7.4) ////////////////////////////////////

export const INITIATIEF_FASE_VOLGORDE: InitiatiefFaseEnum[] = [
    InitiatiefFaseEnum.initiatief,
    InitiatiefFaseEnum.orientatie,
    InitiatiefFaseEnum.realisatie,
    InitiatiefFaseEnum.afronding,
    InitiatiefFaseEnum.afgerond,
    InitiatiefFaseEnum.onbekend,
];

// wel in uitvoering
export const UITVOERINGSFASEN: InitiatiefFaseEnum[] = [
    InitiatiefFaseEnum.realisatie,
    InitiatiefFaseEnum.afronding,
    InitiatiefFaseEnum.afgerond,
];

export const FASE_PLACEHOLDERS: string[] = ['', 'onbekend', 'keuzelijst', 'ontwerp'];

// the phase text of a PMO sheet; afgerond is checked before afrond on purpose (the prototype had that backwards)
export function normalizeFase(text: string | undefined): InitiatiefFaseEnum {
    const t: string = normalizeName(text ?? '');
    if (FASE_PLACEHOLDERS.includes(t)) {
        return InitiatiefFaseEnum.onbekend;
    }
    if (t.includes('afgerond')) {
        return InitiatiefFaseEnum.afgerond;
    }
    if (t.startsWith('initiatie')) {
        return InitiatiefFaseEnum.initiatief;
    }
    if (t.includes('orient')) {
        return InitiatiefFaseEnum.orientatie;
    }
    if (t.includes('realis') || t.includes('uitvoer')) {
        return InitiatiefFaseEnum.realisatie;
    }
    if (t.includes('afrond') || t.includes('afsluit')) {
        return InitiatiefFaseEnum.afronding;
    }
    return InitiatiefFaseEnum.onbekend;
}

//////////////////////////////////// personeel (design spec 7.5, 7.6) ////////////////////////////////////

export type FteBucket = '0 FTE' | '0-1 FTE' | '1-5 FTE' | '5+ FTE';

export const FTE_BUCKETS: FteBucket[] = ['0 FTE', '0-1 FTE', '1-5 FTE', '5+ FTE'];

export function fteBucket(fte: number): FteBucket {
    if (fte <= 0) {
        return '0 FTE';
    }
    if (fte <= 1) {
        return '0-1 FTE';
    }
    if (fte <= 5) {
        return '1-5 FTE';
    }
    return '5+ FTE';
}

// the meetplan value counts as the identified impact, the estimate only when the meetplan is 0
export function geidentificeerdeImpact(initiatief: CabInitiatief): number {
    return initiatief.impactMeetplanFte !== 0 ? initiatief.impactMeetplanFte : initiatief.impactIngeschatFte;
}

// fte expressed in medewerkers through the organisation's deeltijdfactor, 0 without a factor
export function medewerkersVan(fte: number, deeltijdfactor: number | undefined): number {
    return deeltijdfactor === undefined || deeltijdfactor === 0 ? 0 : fte / deeltijdfactor;
}

//////////////////////////////////// uploads ////////////////////////////////////

export type ParsedTargetRule = {
    rule: DashboardTargetRuleEnum;
    value?: number;
};

// the Target column of the Codebook sheet Transformatiedashboard
export function parseTargetRule(text: string | undefined): ParsedTargetRule | undefined {
    const t: string = normalizeName(text ?? '');
    if (t === '') {
        return undefined;
    }
    if (t.includes('groei t.o.v. de eerste meting')) {
        return {rule: DashboardTargetRuleEnum.groeiTovEersteMeting};
    }
    if (t.includes('minimaal gelijk t.o.v. een jaar eerder')) {
        return {rule: DashboardTargetRuleEnum.minimaalGelijkVorigJaar};
    }
    const value: number = Number(t.replace(',', '.'));
    return isNaN(value) ? undefined : {rule: DashboardTargetRuleEnum.vasteWaarde, value: value};
}

export type UploadResultaat = {
    created: number;
    replaced: number;
    warnings: string[];
    errors: string[];
};

export function isUploadResultaat(x: unknown): x is UploadResultaat {
    if (typeof x !== 'object' || x === null) {
        return false;
    }
    const r: Partial<UploadResultaat> = x as Partial<UploadResultaat>;
    return typeof r.created === 'number' && typeof r.replaced === 'number' && Array.isArray(r.warnings) && Array.isArray(r.errors);
}

// YYYY-Qn, used by the dashboard period query params and the upload kwartaal field
export const PERIOD_PATTERN: RegExp = /^\d{4}-Q[1-4]$/;
